import { Link } from 'react-router-dom';
import GuideLayout from '../../layouts/GuideLayout';
import type { TocItem } from '../../layouts/GuideLayout';
import { AffiliateCTA } from '../../components/content/AffiliateCTA';
import { ResourceLink } from '../../components/content/ResourceLink';
import { CalculatorEmbed } from '../../components/content/CalculatorEmbed';

const tocItems: TocItem[] = [
  { id: 'overview', label: 'Overview', level: 1 },
  { id: 'trap-1', label: 'Trap 1: The Archive Rehydration Surprise', level: 1 },
  { id: 'trap-2', label: 'Trap 2: The 2TB Log Analytics Restore Floor', level: 1 },
  { id: 'trap-3', label: 'Trap 3: NAT Gateway Always-On Charges', level: 1 },
  { id: 'trap-4', label: 'Trap 4: Blob Early Deletion Penalties', level: 1 },
  { id: 'trap-5', label: 'Trap 5: Basic Logs Query Cost Trap', level: 1 },
  { id: 'trap-6', label: 'Trap 6: The Hub-Spoke Double NAT Charge', level: 1 },
  { id: 'trap-7', label: 'Trap 7: Inter-Region Bandwidth', level: 1 },
  { id: 'trap-8', label: 'Trap 8: ADF Auto DIU Defaults to 4', level: 1 },
  { id: 'trap-9', label: 'Trap 9: FSLogix Standard Tier Transactions', level: 1 },
  { id: 'trap-10', label: 'Trap 10: Sentinel Data Lake Free Retention Cliff', level: 1 },
  { id: 'trap-11', label: 'Trap 11: Standard HDD vs SSD Transaction Cap', level: 1 },
  { id: 'prevention', label: 'How to Avoid These Traps', level: 1 },
  { id: 'resources', label: 'Official Resources', level: 1 },
];

const bottomCta = (
  <AffiliateCTA
    programme="MeasureUp"
    courseName="AZ-104 Microsoft Azure Administrator"
    url="https://www.measureup.com/microsoft-practice-test-az-104-microsoft-azure-administrator.html"
    price="From £99"
    badgeText="Covers cost management"
  />
);

export default function AzureBillingTrapsPage() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'Azure Billing Traps UK 2026 — Hidden Costs Explained',
            description:
              'Seven Azure pricing traps that surprise engineers — Archive rehydration, Log Analytics 2TB minimum, NAT Gateway always-on charges, and more. UK South GBP pricing.',
            author: { '@type': 'Organization', name: 'AzureCalc.uk' },
            publisher: {
              '@type': 'Organization',
              name: 'AzureCalc.uk',
              url: 'https://www.azure-calc.co.uk',
            },
            dateModified: '2026-04-09',
            url: 'https://www.azure-calc.co.uk/guides/azure-billing-traps-uk',
          }),
        }}
      />
      <GuideLayout
        title="Azure Billing Traps UK 2026 — Hidden Costs Explained"
        description="Seven Azure pricing traps that surprise engineers — Archive rehydration, Log Analytics 2TB minimum, NAT Gateway always-on charges, and more. UK South GBP pricing."
        lastUpdated="April 2026"
        tocItems={tocItems}
        relatedCalculators={[
          { service: 'blob-storage', path: '/blob-storage', label: 'Blob Storage Calculator' },
          { service: 'log-analytics', path: '/log-analytics', label: 'Log Analytics Calculator' },
          { service: 'networking', path: '/networking', label: 'Networking Calculator' },
        ]}
        bottomCta={bottomCta}
        canonical="https://www.azure-calc.co.uk/guides/azure-billing-traps-uk"
        metaTitle="Azure Billing Traps UK 2026 | Hidden Costs Explained | AzureCalc.uk"
        metaDescription="Seven Azure pricing traps that surprise UK engineers: Archive rehydration, Log Analytics 2TB minimum, NAT Gateway always-on, and more. UK South GBP pricing explained."
      >
        <p className="text-[15px] italic mb-8" style={{ color: 'var(--text-tertiary)' }}>
          Seven pricing traps that have burned me or other engineers I know — with the exact cost
          impact and how to avoid them. All prices are UK South GBP from the Azure Retail Prices API.
        </p>

        <p className="text-[12px] italic mb-6" style={{ color: 'var(--text-tertiary)' }}>
          Built and verified by an independent Azure engineer, frustrated with the official calculator.
        </p>

        {/* ── Section 1: Overview ── */}
        <section id="overview">
          <h2>Overview</h2>
          <p>
            I have been burned by Azure pricing traps three times. Each time, the bill was 10× what I
            estimated. Each time, the trap was documented — but buried in a footnote or pricing page
            footnote that you only find after the invoice arrives.
          </p>
          <p>
            This guide collects every trap I have personally encountered or verified against real
            invoices, with the exact cost impact and how to avoid it. These are not theoretical edge
            cases; they are mistakes that production engineering teams make regularly because the
            pricing model is unintuitive.
          </p>
          <p>
            All figures below are current UK South GBP pricing from the{' '}
            <Link to="/methodology" className="text-[#0078d4] hover:underline transition-colors">
              Azure Retail Prices API
            </Link>
            , updated nightly. If you find an error, email me and I will verify it against the API
            within 24 hours.
          </p>

          <div className="not-prose border rounded p-4 my-6" style={{ backgroundColor: 'var(--surface-container-low)', borderColor: '#0078d4' }}>
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-[#0078d4] text-[20px] shrink-0 mt-0.5">
                warning
              </span>
              <div className="text-[13px]" style={{ color: 'var(--text-primary)' }}>
                <strong>Key principle:</strong> Azure pricing is correct but not intuitive. The traps
                below are all documented somewhere — but never where you look when estimating costs.
              </div>
            </div>
          </div>
        </section>

        {/* ── Section 2: Trap 1 ── */}
        <section id="trap-1">
          <h2>Trap 1: The Archive Rehydration Surprise</h2>
          <p>
            Archive tier storage is roughly 20× cheaper than Hot tier to store — approximately
            £0.001/GB/month vs £0.018/GB/month. This makes it look like the obvious choice for old
            data. It is not.
          </p>
          <p>
            The trap: retrieving data from Archive tier costs{' '}
            <strong>£0.0157/GB plus rehydration time</strong> (hours, not seconds). If you restore 1TB
            of archived data once, you pay £16.07 in retrieval fees. That erases{' '}
            <strong>13+ months</strong> of storage savings in a single operation.
          </p>

          <div className="not-prose border rounded p-5 my-4" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <p className="text-[13px] font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Real example</p>
            <p className="text-[13px] mb-3" style={{ color: 'var(--text-tertiary)' }}>
              In March 2025, I archived 500GB of old logs thinking I would save £8.50/month. I needed
              them 3 months later for an audit. Rehydration cost: £7.85. My "savings": £25.50 over 3
              months. Retrieval cost: £7.85. Net savings after one restore: £17.65. Not worth the
              operational complexity.
            </p>
            <p className="text-[13px] italic" style={{ color: 'var(--text-tertiary)' }}>
              The second restore would have made Archive tier more expensive than keeping the data
              in Hot tier the entire time.
            </p>
          </div>

          <div className="not-prose rounded p-4 my-6" style={{ backgroundColor: 'rgba(160, 64, 64, 0.1)', border: '1px solid rgba(160, 64, 64, 0.4)' }}>
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-[#e04040] text-[20px] shrink-0 mt-0.5">
                error
              </span>
              <div>
                <p className="text-[13px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>The break-even math</p>
                <p className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                  Archive storage saves ~£0.017/GB/month. Archive retrieval costs £0.0157/GB. If you
                  restore data more than once every 11 months, Archive is more expensive than Hot.
                </p>
              </div>
            </div>
          </div>

          <h3>How to avoid it</h3>
          <div className="not-prose flex flex-col gap-2 my-4">
            {[
              'Only archive data you are legally required to keep but expect to read zero times',
              'If you might read it once, use Cold tier (£0.004/GB/month, no rehydration fee)',
              'If you might read it twice or more, keep it in Hot or Cool tier',
              'Document your Archive retention policy — and enforce it with lifecycle management rules',
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 px-4 py-2.5 rounded"
                style={{ backgroundColor: 'rgba(16, 124, 16, 0.08)', border: '1px solid rgba(16, 124, 16, 0.35)' }}
              >
                <span className="material-symbols-outlined text-[#107c10] text-[16px] shrink-0">
                  check_circle
                </span>
                <span className="text-[13px]" style={{ color: 'var(--text-primary)' }}>{item}</span>
              </div>
            ))}
          </div>

          <div className="not-prose mt-4">
            <CalculatorEmbed service="blob-storage" />
          </div>
        </section>

        {/* ── Section 3: Trap 2 ── */}
        <section id="trap-2">
          <h2>Trap 2: The 2TB Log Analytics Restore Floor</h2>
          <p>
            Log Analytics Restore operations have a{' '}
            <strong>2,000GB minimum billing floor</strong>. Restore 1GB of archived data, you are
            billed for 2,000GB. At PAYG rates (£2.167/GB), that is approximately £4,334 for
            restoring 1GB of data.
          </p>

          <div className="not-prose border rounded p-5 my-4" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <p className="text-[13px] font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Real example from Reddit</p>
            <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
              A user DM'd me: they restored 50MB of archived security logs for a compliance check.
              The invoice line item: £4,334. They thought the calculator was wrong. It was not —
              they triggered the 2TB minimum billing floor. The Restore operation is designed for
              bulk data recovery, not selective queries.
            </p>
          </div>

          <div className="not-prose rounded p-4 my-6" style={{ backgroundColor: 'rgba(160, 64, 64, 0.1)', border: '1px solid rgba(160, 64, 64, 0.4)' }}>
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-[#e04040] text-[20px] shrink-0 mt-0.5">
                error
              </span>
              <div>
                <p className="text-[13px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>Critical distinction</p>
                <p className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                  Restore is for bringing archived data back to interactive (Analytics) tier. Search
                  Jobs are for querying archived data in place. If you need to investigate archived
                  logs, use Search Jobs. Never use Restore for small data pulls.
                </p>
              </div>
            </div>
          </div>

          <h3>How to avoid it</h3>
          <div className="not-prose flex flex-col gap-2 my-4">
            {[
              'Use Search Jobs to investigate archived data — no minimum billing',
              'Only use Restore for bulk recovery of TB-scale datasets',
              'Train your security team: "compliance check" does not mean Restore',
              'Set up Azure Policy alerts if someone attempts a Restore operation',
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 px-4 py-2.5 rounded"
                style={{ backgroundColor: 'rgba(16, 124, 16, 0.08)', border: '1px solid rgba(16, 124, 16, 0.35)' }}
              >
                <span className="material-symbols-outlined text-[#107c10] text-[16px] shrink-0">
                  check_circle
                </span>
                <span className="text-[13px]" style={{ color: 'var(--text-primary)' }}>{item}</span>
              </div>
            ))}
          </div>

          <div className="not-prose mt-4">
            <CalculatorEmbed service="log-analytics" />
          </div>
        </section>

        {/* ── Section 4: Trap 3 ── */}
        <section id="trap-3">
          <h2>Trap 3: NAT Gateway Always-On Charges</h2>
          <p>
            NAT Gateway has <strong>no pause, stop, or idle state</strong>. It bills every hour until
            deleted. An idle NAT Gateway costs approximately £33.12/month in UK South forever.
          </p>
          <p>
            This is particularly painful in dev/test environments where engineers create a NAT Gateway
            for testing, forget about it, and leave it running for months.
          </p>

          <div className="not-prose border rounded p-5 my-4" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <p className="text-[13px] font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>My personal burn</p>
            <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
              I left a NAT Gateway running in a test environment for 4 months. Cost: £132.48. I used
              it for approximately 2 hours of actual testing. The rest of the time it was idle,
              burning £0.045/hour for no purpose.
            </p>
          </div>

          <div className="not-prose overflow-x-auto my-6">
            <table className="w-full text-[13px] border-collapse">
              <thead>
                <tr style={{ borderBottom: '2px solid var(--outline-variant)' }}>
                  <th className="text-left py-2 pr-6 font-semibold" style={{ color: 'var(--text-tertiary)' }}>Scenario</th>
                  <th className="text-right py-2 font-semibold" style={{ color: 'var(--text-tertiary)' }}>Monthly cost</th>
                  <th className="text-right py-2 font-semibold" style={{ color: 'var(--text-tertiary)' }}>Annual cost</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Idle NAT Gateway (0GB processed)', '£33.12', '£397.44'],
                  ['NAT Gateway + Azure Firewall idle', '~£290', '~£3,480'],
                  ['NAT Gateway (100GB processed)', '£33.12 + £3.50', '£439.44'],
                ].map(([scenario, monthly, annual]) => (
                  <tr key={scenario} style={{ borderBottom: '1px solid var(--surface-container-high)' }}>
                    <td className="py-2 pr-6" style={{ color: 'var(--text-secondary)' }}>{scenario}</td>
                    <td className="py-2 pr-6 text-right" style={{ color: 'var(--text-primary)' }}>{monthly}</td>
                    <td className="py-2 text-right" style={{ color: 'var(--text-primary)' }}>{annual}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3>How to avoid it</h3>
          <div className="not-prose flex flex-col gap-2 my-4">
            {[
              'Delete NAT Gateways in dev/test environments when not in use',
              'Use Azure Policy to alert on NAT Gateways older than 7 days in non-production subscriptions',
              'For production, set up tagging policy: CostCenter tag required, monthly review of untagged NAT Gateways',
              'Consider Azure Firewall + NAT Gateway combination carefully — idle cost is ~£290/month',
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 px-4 py-2.5 rounded"
                style={{ backgroundColor: 'rgba(16, 124, 16, 0.08)', border: '1px solid rgba(16, 124, 16, 0.35)' }}
              >
                <span className="material-symbols-outlined text-[#107c10] text-[16px] shrink-0">
                  check_circle
                </span>
                <span className="text-[13px]" style={{ color: 'var(--text-primary)' }}>{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── Section 5: Trap 4 ── */}
        <section id="trap-4">
          <h2>Trap 4: Blob Storage Early Deletion Penalties</h2>
          <p>
            Cool, Cold, and Archive tiers have minimum retention periods. Delete data early and you
            are charged for the full minimum period anyway.
          </p>

          <div className="not-prose overflow-x-auto my-6">
            <table className="w-full text-[13px] border-collapse">
              <thead>
                <tr style={{ borderBottom: '2px solid var(--outline-variant)' }}>
                  <th className="text-left py-2 pr-6 font-semibold" style={{ color: 'var(--text-tertiary)' }}>Tier</th>
                  <th className="text-left py-2 pr-6 font-semibold" style={{ color: 'var(--text-tertiary)' }}>Minimum retention</th>
                  <th className="text-left py-2 font-semibold" style={{ color: 'var(--text-tertiary)' }}>Penalty if deleted early</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Hot', 'None', 'None'],
                  ['Cool', '30 days', 'Charged for 30 days regardless of actual storage time'],
                  ['Cold', '90 days', 'Charged for 90 days regardless of actual storage time'],
                  ['Archive', '180 days', 'Charged for 180 days regardless of actual storage time'],
                ].map(([tier, min, penalty]) => (
                  <tr key={tier} style={{ borderBottom: '1px solid var(--surface-container-high)' }}>
                    <td className="py-2 pr-6" style={{ color: 'var(--text-secondary)' }}>{tier}</td>
                    <td className="py-2 pr-6" style={{ color: 'var(--text-tertiary)' }}>{min}</td>
                    <td className="py-2" style={{ color: 'var(--text-tertiary)' }}>{penalty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="not-prose border rounded p-5 my-4" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <p className="text-[13px] font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Real example</p>
            <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
              Moved 2TB to Cool tier for a "temporary" archive. Deleted after 15 days because the
              project scope changed. Charged for 30 days anyway — extra cost of approximately
              £240 for data I did not actually store. The early deletion penalty for 2TB in Cool
              tier is significant.
            </p>
          </div>

          <h3>How to avoid it</h3>
          <div className="not-prose flex flex-col gap-2 my-4">
            {[
              'Only use tiered storage for data you know you will keep for the minimum period',
              'Use Hot tier for anything with uncertain retention',
              'Set lifecycle management policies, but review them quarterly — they can trigger unexpected tiering',
              'Document tier selection criteria in your team runbook',
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 px-4 py-2.5 rounded"
                style={{ backgroundColor: 'rgba(16, 124, 16, 0.08)', border: '1px solid rgba(16, 124, 16, 0.35)' }}
              >
                <span className="material-symbols-outlined text-[#107c10] text-[16px] shrink-0">
                  check_circle
                </span>
                <span className="text-[13px]" style={{ color: 'var(--text-primary)' }}>{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── Section 6: Trap 5 ── */}
        <section id="trap-5">
          <h2>Trap 5: Basic Logs Query Cost Trap</h2>
          <p>
            Basic Logs are 78% cheaper to ingest than Analytics Logs (£0.46/GB vs £2.13/GB). This
            makes them look attractive for verbose logs like AppServiceHTTPLogs or NetworkFlow.
          </p>
          <p>
            The trap: <strong>Basic Logs charge £0.0046/GB per query</strong>. Query the data 100
            times and you have paid £0.46/GB in query costs — the same as Analytics Logs ingestion.
            Plus Basic Logs have no alert rules and only 8-day retention.
          </p>

          <div className="not-prose rounded p-4 my-6" style={{ backgroundColor: 'rgba(160, 64, 64, 0.1)', border: '1px solid rgba(160, 64, 64, 0.4)' }}>
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-[#e04040] text-[20px] shrink-0 mt-0.5">
                error
              </span>
              <div>
                <p className="text-[13px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>The break-even math</p>
                <p className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                  Analytics Logs: £2.13/GB ingestion, free queries. Basic Logs: £0.46/GB ingestion,
                  £0.0046/GB per query. Querying more than 363× per month (12× daily) makes Basic
                  more expensive than Analytics for the same data volume.
                </p>
              </div>
            </div>
          </div>

          <h3>How to avoid it</h3>
          <div className="not-prose flex flex-col gap-2 my-4">
            {[
              'Use Basic only for logs queried less than weekly',
              'If you need alerts or frequent dashboards, Analytics tier is cheaper overall',
              'Track your query frequency in Log Analytics Workspace Insights — if you query Basic logs daily, switch to Analytics',
              'Consider Auxiliary Logs (new) for compliance data — even cheaper at £0.04/GB but no direct querying',
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 px-4 py-2.5 rounded"
                style={{ backgroundColor: 'rgba(16, 124, 16, 0.08)', border: '1px solid rgba(16, 124, 16, 0.35)' }}
              >
                <span className="material-symbols-outlined text-[#107c10] text-[16px] shrink-0">
                  check_circle
                </span>
                <span className="text-[13px]" style={{ color: 'var(--text-primary)' }}>{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── Section 7: Trap 6 ── */}
        <section id="trap-6">
          <h2>Trap 6: The Hub-Spoke Double NAT Gateway Charge</h2>
          <p>
            In hub-spoke architecture, if both hub VNet and spoke VNet have NAT Gateways for outbound
            internet, traffic from spoke → internet flows through both. 1GB egress through two NAT
            Gateways = <strong>2GB of NAT Gateway processing billed</strong>.
          </p>

          <div className="not-prose border rounded p-5 my-4" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <p className="text-[13px] font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Real example from NHS cloud architecture</p>
            <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
              Hub VNet with NAT Gateway for shared services. Each spoke also had NAT Gateway for
              "isolation." 10TB/month egress was billed as 20TB. Extra cost: £300+/month completely
              invisible in the official calculator because it depends on your routing architecture,
              not your data volume.
            </p>
          </div>

          <h3>How to avoid it</h3>
          <div className="not-prose flex flex-col gap-2 my-4">
            {[
              'Centralize NAT Gateway in hub VNet only',
              'Spokes use hub\'s NAT Gateway for internet egress via VNet peering',
              'Document this in your architecture review — NAT Gateway placement is a cost decision',
              'Use Azure Firewall for hub-spoke if you need advanced outbound filtering — single egress point',
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 px-4 py-2.5 rounded"
                style={{ backgroundColor: 'rgba(16, 124, 16, 0.08)', border: '1px solid rgba(16, 124, 16, 0.35)' }}
              >
                <span className="material-symbols-outlined text-[#107c10] text-[16px] shrink-0">
                  check_circle
                </span>
                <span className="text-[13px]" style={{ color: 'var(--text-primary)' }}>{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── Section 8: Trap 7 ── */}
        <section id="trap-7">
          <h2>Trap 7: Inter-Region Bandwidth Charges</h2>
          <p>
            Data transferred between UK South and UK West is charged at approximately £0.016/GB. Same
            country, different Azure region = billed from the first byte. Many engineers assume
            "same geo = free." It is not.
          </p>

          <div className="not-prose border rounded p-5 my-4" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <p className="text-[13px] font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Real example</p>
            <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
              DR setup: primary in UK South, replica in UK West. 500GB/day replication. Expected
              cost: storage only. Actual cost: storage + £43.50/day bandwidth = £1,305/month
              surprise. The team assumed intra-UK transfer was free.
            </p>
          </div>

          <div className="not-prose rounded p-4 my-6" style={{ backgroundColor: 'rgba(0,120,212,0.08)', border: '1px solid rgba(0,120,212,0.35)' }}>
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-[#0078d4] text-[20px] shrink-0 mt-0.5">
                info
              </span>
              <div className="text-[13px]" style={{ color: 'var(--text-primary)' }}>
                <strong>What is free:</strong> Availability Zones within UK South (intra-zone
                transfer). <strong>What costs money:</strong> UK South ↔ UK West, UK South ↔ West
                Europe, any inter-region combination.
              </div>
            </div>
          </div>

          <h3>How to avoid it</h3>
          <div className="not-prose flex flex-col gap-2 my-4">
            {[
              'Use Availability Zones within UK South for HA — intra-zone transfer is free',
              'Only use multi-region for true DR requirements, and budget for bandwidth explicitly',
              'If you must replicate inter-region, use compression — Azure charges for bytes transferred, not logical data',
              'Document bandwidth costs in your DR runbook — they are predictable but never zero',
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 px-4 py-2.5 rounded"
                style={{ backgroundColor: 'rgba(16, 124, 16, 0.08)', border: '1px solid rgba(16, 124, 16, 0.35)' }}
              >
                <span className="material-symbols-outlined text-[#107c10] text-[16px] shrink-0">
                  check_circle
                </span>
                <span className="text-[13px]" style={{ color: 'var(--text-primary)' }}>{item}</span>
              </div>
            ))}
          </div>

          <div className="not-prose mt-4">
            <CalculatorEmbed service="bandwidth" />
          </div>
        </section>

        {/* ── Section 9: Trap 8 ── */}
        <section id="trap-8">
          <h2>Trap 8: ADF "Auto" DIU Defaults to 4</h2>
          <p>
            Azure Data Factory Copy activities default to <strong>Auto DIU selection</strong>, which
            means ADF picks the Data Integration Unit count at runtime. In practice, Auto almost
            always resolves to 4 DIUs — even for simple, small-data pipelines that would run fine on 2.
          </p>
          <p>
            The billing impact: 4 DIUs × duration × rate vs 2 DIUs × duration × rate. Dev and test
            pipelines running hundreds of small copies per day accumulate 2× the expected cost.
          </p>

          <div className="not-prose border rounded p-5 my-4" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <p className="text-[13px] font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Real example from a reader</p>
            <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
              A reader emailed after their ADF estimate came out 2× what they expected. Their dev
              pipeline ran 500 Copy activities per day, each lasting ~30 seconds. Auto DIU = 4.
              Manual DIU = 2. The difference: £18/day vs £9/day — £270/month on a dev pipeline.
              Production pipelines genuinely benefit from Auto, but dev/test pipelines almost never do.
            </p>
          </div>

          <h3>How to avoid it</h3>
          <div className="not-prose flex flex-col gap-2 my-4">
            {[
              'Set DIU = 2 explicitly on all dev and test Copy activities',
              'Only use Auto DIU on production pipelines moving TB-scale data',
              'Use the ADF monitoring view to check actual DIU usage — most small copies use 4 but could use 2',
              'Add a pipeline parameter for DIU count so dev/prod environments can differ without code changes',
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 px-4 py-2.5 rounded"
                style={{ backgroundColor: 'rgba(16, 124, 16, 0.08)', border: '1px solid rgba(16, 124, 16, 0.35)' }}
              >
                <span className="material-symbols-outlined text-[#107c10] text-[16px] shrink-0">check_circle</span>
                <span className="text-[13px]" style={{ color: 'var(--text-primary)' }}>{item}</span>
              </div>
            ))}
          </div>

          <div className="not-prose mt-4">
            <CalculatorEmbed service="data-factory" />
          </div>
        </section>

        {/* ── Section 10: Trap 9 ── */}
        <section id="trap-9">
          <h2>Trap 9: FSLogix Standard Tier Transaction Costs</h2>
          <p>
            Azure Files Standard tier for FSLogix profile storage looks attractive at approximately
            £0.056/GB/month — much cheaper than Premium at £0.192/GB/month provisioned. For a 50-user
            deployment with 20GB profiles, that is £56/month vs £192/month. An obvious choice.
          </p>
          <p>
            The trap: Standard tier charges per transaction.{' '}
            <strong>
              An active VDI workload generates tens of thousands of read and write transactions per
              user login
            </strong>{' '}
            — profile loading, registry reads, folder redirects. The transaction cost can easily
            exceed the storage savings within the first month.
          </p>

          <div className="not-prose rounded p-4 my-6" style={{ backgroundColor: 'rgba(160, 64, 64, 0.1)', border: '1px solid rgba(160, 64, 64, 0.4)' }}>
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-[#e04040] text-[20px] shrink-0 mt-0.5">error</span>
              <div>
                <p className="text-[13px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>The break-even math</p>
                <p className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                  Premium is provisioned capacity, no transactions. Standard is per-GB plus
                  £0.0113/10,000 write ops and £0.00113/10,000 read ops. An active desktop generating
                  500K write ops/day crosses the Premium equivalent cost within 3–5 days of the month.
                  After that, every additional transaction makes Standard more expensive than Premium.
                </p>
              </div>
            </div>
          </div>

          <h3>How to avoid it</h3>
          <div className="not-prose flex flex-col gap-2 my-4">
            {[
              'Use Premium for any deployment with daily active users — the provisioned model is predictable',
              'Reserve Standard for test/dev environments with infrequent logins',
              'Use the AVD calculator to model Standard vs Premium including transaction estimates',
              'Monitor Azure Files metrics (Transactions by response type) before committing to Standard in production',
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 px-4 py-2.5 rounded"
                style={{ backgroundColor: 'rgba(16, 124, 16, 0.08)', border: '1px solid rgba(16, 124, 16, 0.35)' }}
              >
                <span className="material-symbols-outlined text-[#107c10] text-[16px] shrink-0">check_circle</span>
                <span className="text-[13px]" style={{ color: 'var(--text-primary)' }}>{item}</span>
              </div>
            ))}
          </div>

          <div className="not-prose mt-4">
            <CalculatorEmbed service="virtual-desktop" />
          </div>
        </section>

        {/* ── Section 11: Trap 10 ── */}
        <section id="trap-10">
          <h2>Trap 10: Sentinel Data Lake Free Retention Cliff</h2>
          <p>
            Microsoft Sentinel's Data Lake tier (formerly called Auxiliary Logs) offers{' '}
            <strong>3 months of free retention</strong>. This sounds generous — and it is, until
            month 4, when the retention billing clock starts. The billing model is different from
            Analytics Logs, so teams that have estimated costs using the Analytics Logs retention
            rate will be surprised.
          </p>
          <p>
            The practical trap: Sentinel deployments that were architected around "3 months free"
            as a long-term retention strategy discover in month 4 that they need to either pay for
            extended retention or export and delete the data — which creates its own operational overhead.
          </p>

          <div className="not-prose border rounded p-5 my-4" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <p className="text-[13px] font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>The retention model explained</p>
            <p className="text-[13px] mb-2" style={{ color: 'var(--text-tertiary)' }}>
              Analytics Logs: 31 days free, then £0.114/GB/month retention. Data Lake (Auxiliary):
              3 months (90 days) free, then retention billing starts — at a different rate from
              Analytics. The two models are not interchangeable for cost planning.
            </p>
            <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
              A 12-month retention requirement on 100GB/day ingestion means 9 months of paid
              retention on Data Lake vs 11 months on Analytics. Calculate both before choosing a tier.
            </p>
          </div>

          <h3>How to avoid it</h3>
          <div className="not-prose flex flex-col gap-2 my-4">
            {[
              'Model the full 12-month retention cost, not just ingestion, before choosing a tier',
              'Set a calendar reminder for month 3 to review Data Lake retention and decide: extend, export, or delete',
              'Use Azure Log Analytics Workspace Insights to track data volume before it exceeds your free tier',
              'For compliance data requiring 2+ year retention, evaluate Azure Blob Storage Archive vs Data Lake retention cost',
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 px-4 py-2.5 rounded"
                style={{ backgroundColor: 'rgba(16, 124, 16, 0.08)', border: '1px solid rgba(16, 124, 16, 0.35)' }}
              >
                <span className="material-symbols-outlined text-[#107c10] text-[16px] shrink-0">check_circle</span>
                <span className="text-[13px]" style={{ color: 'var(--text-primary)' }}>{item}</span>
              </div>
            ))}
          </div>

          <div className="not-prose mt-4">
            <CalculatorEmbed service="log-analytics" />
          </div>
        </section>

        {/* ── Section 12: Trap 11 ── */}
        <section id="trap-11">
          <h2>Trap 11: Standard HDD vs Standard SSD Transaction Cap Inversion</h2>
          <p>
            Standard HDD managed disks are cheaper per-GB than Standard SSD. For data disks storing
            cold, infrequently accessed data, HDD looks like the obvious choice. The trap is in the
            transaction billing.
          </p>
          <p>
            Standard HDD charges <strong>£0.000494 per 10,000 transactions</strong>. Standard SSD
            charges £0.000198 per 10,000 transactions. On a disk with moderate I/O — a database log,
            a file server, a VM with frequent small writes — the Standard HDD transaction cost can
            overtake the SSD storage savings within a single month.
          </p>

          <div className="not-prose rounded p-4 my-6" style={{ backgroundColor: 'rgba(160, 64, 64, 0.1)', border: '1px solid rgba(160, 64, 64, 0.4)' }}>
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-[#e04040] text-[20px] shrink-0 mt-0.5">error</span>
              <div>
                <p className="text-[13px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>The inversion</p>
                <p className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                  At identical storage capacity and IOPS, HDD can be more expensive than SSD once
                  transaction costs are included. This is the opposite of what most engineers expect.
                  The safe default is Standard SSD for any disk with regular read/write patterns —
                  even if storage capacity alone would favour HDD.
                </p>
              </div>
            </div>
          </div>

          <h3>How to avoid it</h3>
          <div className="not-prose flex flex-col gap-2 my-4">
            {[
              'Use Standard SSD as the default for all non-archival data disks',
              'Reserve Standard HDD for disks that are written once and read rarely (backups, cold exports)',
              'Check Azure Cost Management disk transaction metrics before assuming HDD is cheaper',
              'For high-IOPS workloads, Premium SSD is often cheaper than Standard SSD with high transactions',
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 px-4 py-2.5 rounded"
                style={{ backgroundColor: 'rgba(16, 124, 16, 0.08)', border: '1px solid rgba(16, 124, 16, 0.35)' }}
              >
                <span className="material-symbols-outlined text-[#107c10] text-[16px] shrink-0">check_circle</span>
                <span className="text-[13px]" style={{ color: 'var(--text-primary)' }}>{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── Section 13: Prevention ── */}
        <section id="prevention">
          <h2>How to Avoid These Traps</h2>
          <p>
            Before provisioning any Azure resource that has a metered cost, run through this
            checklist. It takes 2 minutes and has saved me thousands of pounds.
          </p>

          <div className="not-prose flex flex-col gap-3 my-6">
            {[
              {
                question: 'Will I ever need to restore Archive data?',
                action: 'If yes, use Cold tier instead',
                trap: 'Trap 1',
              },
              {
                question: 'Am I querying Basic Logs more than weekly?',
                action: 'If yes, switch to Analytics tier',
                trap: 'Trap 5',
              },
              {
                question: 'Is this NAT Gateway in a dev/test environment?',
                action: 'If yes, set calendar reminder to delete it',
                trap: 'Trap 3',
              },
              {
                question: 'Am I tiering data with uncertain retention?',
                action: 'If yes, keep in Hot tier until retention is certain',
                trap: 'Trap 4',
              },
              {
                question: 'Do I have NAT Gateways in both hub and spoke?',
                action: 'If yes, remove spoke NAT Gateway immediately',
                trap: 'Trap 6',
              },
              {
                question: 'Is my DR strategy multi-region?',
                action: 'If yes, add bandwidth costs to the budget explicitly',
                trap: 'Trap 7',
              },
              {
                question: 'Is my ADF Copy activity using Auto DIU in dev/test?',
                action: 'If yes, set DIU = 2 explicitly to halve compute costs',
                trap: 'Trap 8',
              },
              {
                question: 'Are my FSLogix profiles on Azure Files Standard tier?',
                action: 'If users log in daily, compare transaction costs against Premium provisioned',
                trap: 'Trap 9',
              },
              {
                question: 'Am I using Sentinel Data Lake tier beyond 3 months?',
                action: 'If yes, model the full retention cost at month 4+ before committing',
                trap: 'Trap 10',
              },
              {
                question: 'Did I choose Standard HDD to save on storage?',
                action: 'If the disk has regular I/O, check transaction costs — SSD may be cheaper',
                trap: 'Trap 11',
              },
            ].map((check) => (
              <div
                key={check.question}
                className="flex gap-4 border rounded p-4"
                style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}
              >
                <span className="material-symbols-outlined text-[#0078d4] text-[22px] shrink-0 mt-0.5">
                  help
                </span>
                <div>
                  <p className="text-[14px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>{check.question}</p>
                  <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                    {check.action} <span style={{ color: 'var(--text-tertiary)' }}>(addresses {check.trap})</span>
                  </p>
                </div>
              </div>
            ))}
          </div>

          <p>
            The{' '}
            <Link to="/methodology" className="text-[#0078d4] hover:underline transition-colors">
              Methodology page
            </Link>{' '}
            explains how I verify every price on this site against the Azure Retail Prices API. The{' '}
            <Link
              to="/guides/invoice-reconciliation"
              className="text-[#0078d4] hover:underline transition-colors"
            >
              Invoice Reconciliation guide
            </Link>{' '}
            shows how to verify the calculator against your actual Azure bill.
          </p>
        </section>

        {/* ── Section 10: Resources ── */}
        <section id="resources">
          <h2>Official Resources</h2>
          <div className="not-prose flex flex-col gap-2 my-4">
            <ResourceLink
              title="Azure Blob Storage pricing"
              url="https://azure.microsoft.com/en-gb/pricing/details/storage/blobs/"
              source="Microsoft Azure"
            />
            <ResourceLink
              title="Azure Log Analytics pricing"
              url="https://azure.microsoft.com/en-gb/pricing/details/monitor/"
              source="Microsoft Azure"
            />
            <ResourceLink
              title="NAT Gateway pricing"
              url="https://azure.microsoft.com/en-gb/pricing/details/virtual-network/"
              source="Microsoft Azure"
            />
            <ResourceLink
              title="Azure Bandwidth pricing"
              url="https://azure.microsoft.com/en-gb/pricing/details/bandwidth/"
              source="Microsoft Azure"
            />
          </div>
        </section>
      </GuideLayout>
    </>
  );
}
